import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { formatPriceFR, formatNumberFR, formatPercentFR } from '../utils/formatFR';

// Classe utilitaire — polices du Dashboard chargées depuis index.html (Outfit + Manrope)
const HEADING_FONT = { fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.01em' };
const BODY_FONT = { fontFamily: "'Manrope', sans-serif" };

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Utilitaires de formatage ─────────────────────────────────────────
// formatCurrency : format FR complet (jamais "K€" ni "M€"). Si pas de valeur → "—"
const formatCurrency = (v) => {
  if (v === null || v === undefined || v === 0 || v === '') return '—';
  return formatPriceFR(v);
};

const getCurrentUserName = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    const prenom = u.prenom || u.first_name || '';
    return prenom || '';
  } catch {
    return '';
  }
};

const getTodayGreeting = () => {
  const d = new Date();
  const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
  const months = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
  return `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]}`;
};

// lot DL — pastille d'initiales : palette sans violet, couleur choisie par l'initiale.
const DEGRADES_CONTACT = [
  'bg-gradient-to-br from-blue-500 to-blue-600',
  'bg-gradient-to-br from-pink-500 to-pink-600',
  'bg-gradient-to-br from-orange-500 to-orange-600',
  'bg-gradient-to-br from-teal-500 to-teal-600',
];
const degradeContact = (nom) =>
  DEGRADES_CONTACT[(String(nom || '?').charCodeAt(0) || 0) % DEGRADES_CONTACT.length];

const getInitials = (name) => {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/);
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
};

// ─── Composant KPI card ────────────────────────────────────────────────
const KpiCard = ({ icon, iconBg, iconColor, label, value, trend, trendColor, testId, onClick, sublabel }) => {
  const clickable = typeof onClick === 'function';
  return (
    <div
      data-testid={testId}
      onClick={clickable ? onClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      className={`bg-white border border-slate-200 rounded-xl p-5 transition-all ${
        clickable
          ? 'cursor-pointer hover:border-blue-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2'
          : 'hover:border-slate-300 hover:shadow-sm'
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className={`w-9 h-9 rounded-lg ${iconBg} flex items-center justify-center`}>
          <i className={`bi ${icon} ${iconColor}`}></i>
        </div>
        {trend && (
          <span className={`text-xs font-medium inline-flex items-center gap-1 ${trendColor}`}>
            {trend}
          </span>
        )}
      </div>
      <div className="text-xs text-slate-500 mb-1 flex items-center gap-1.5" style={BODY_FONT}>
        <span>{label}</span>
        {clickable && <i className="bi bi-arrow-right text-[10px] text-slate-400"></i>}
      </div>
      <div className="text-2xl font-bold text-slate-900" style={HEADING_FONT} data-testid={testId ? `${testId}-value` : undefined}>{value}</div>
      {sublabel && <div className="text-[11px] text-slate-400 mt-1.5 leading-tight" style={BODY_FONT}>{sublabel}</div>}
    </div>
  );
};

// ─── Empty state générique ─────────────────────────────────────────────
const EmptyState = ({ icon, title, subtitle, cta, onCta }) => (
  <div className="p-10 text-center">
    <div className="w-14 h-14 rounded-full bg-slate-50 mx-auto flex items-center justify-center mb-3">
      <i className={`bi ${icon} text-slate-400 text-2xl`}></i>
    </div>
    <h4 className="font-semibold text-slate-900" style={HEADING_FONT}>{title}</h4>
    <p className="text-sm text-slate-500 mt-1" style={BODY_FONT}>{subtitle}</p>
    {cta && (
      <button onClick={onCta} className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 bg-[#0062CC] hover:bg-[#0056b3] text-white text-sm font-medium rounded-lg transition-colors">
        <i className="bi bi-plus-lg"></i>{cta}
      </button>
    )}
  </div>
);

// ─── Donut chart SVG (répartition) ────────────────────────────────────
const DonutChart = ({ entries, total }) => {
  // Couleurs cohérentes (palette Komerces)
  const palette = ['#007BFF', '#10B981', '#F59E0B', '#0D9488', '#EC4899', '#06B6D4', '#F97316'];
  let offset = 0;
  const segments = entries.map((e, i) => {
    const pct = total > 0 ? (e.count / total) * 100 : 0;
    const dash = `${pct.toFixed(2)} ${(100 - pct).toFixed(2)}`;
    const seg = (
      <circle
        key={`${e.label}-${i}`}
        cx="21" cy="21" r="15.915"
        fill="none"
        stroke={palette[i % palette.length]}
        strokeWidth="5"
        strokeDasharray={dash}
        strokeDashoffset={-offset}
      />
    );
    offset += pct;
    return seg;
  });

  return (
    <div className="relative flex items-center justify-center my-4">
      <svg width="170" height="170" viewBox="0 0 42 42" className="transform -rotate-90">
        <circle cx="21" cy="21" r="15.915" fill="none" stroke="#E2E8F0" strokeWidth="5"></circle>
        {segments}
      </svg>
      <div className="absolute text-center">
        <div className="text-xs text-slate-500">Total</div>
        <div className="text-xl font-bold text-slate-900" style={HEADING_FONT}>{total}</div>
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════
// Dashboard principal
// ═════════════════════════════════════════════════════════════════════
const Dashboard = ({
  contactsCount = 0,
  contacts = [],
  demandesContactCount = 0,
  demandesContact = [],
  properties = [],
  goToPropertiesList,
  startNewProperty,
  startNewContact,
  showContactsView,
  showDemandesContactView,
}) => {
  const [stats, setStats] = useState(null);
  const [actionable, setActionable] = useState(null);
  const [loyerFocused, setLoyerFocused] = useState(false);
  const [prixFocused, setPrixFocused] = useState(false);
  const [repartitionKey, setRepartitionKey] = useState('operation');
  const [calcLoyer, setCalcLoyer] = useState(25000);
  const [calcPrix, setCalcPrix] = useState(320000);
  const [regeneratingInsights, setRegeneratingInsights] = useState(false);
  const [toastMsg, setToastMsg] = useState(null);
  const navigate = useNavigate();

  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 3500);
  };

  const handleInsightAction = (insight) => {
    const code = insight?.action_code;
    const param = insight?.action_param;
    if (!code) {
      showToast('Aucune action liée à cet insight');
      return;
    }
    switch (code) {
      case 'revise_prices':
        navigate('/properties?sort=oldest&status=active');
        break;
      case 'add_photos':
        navigate('/properties?filter=no_photos');
        break;
      case 'prospect_city':
        if (param) {
          navigate(`/contacts?ville=${encodeURIComponent(param)}`);
        } else {
          navigate('/contacts');
        }
        break;
      case 'relaunch_hot_leads':
        navigate('/contacts?statut_lead=chaud');
        break;
      case 'view_matching': {
        const el = document.querySelector('[data-testid="dashboard-matching"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        break;
      }
      case 'create_property':
        if (typeof startNewProperty === 'function') startNewProperty();
        else navigate('/properties/new');
        break;
      case 'create_contact':
        if (typeof startNewContact === 'function') startNewContact();
        else navigate('/contacts/new');
        break;
      case 'launch_campaign':
        navigate('/marketing');
        break;
      case 'view_properties':
        if (typeof goToPropertiesList === 'function') goToPropertiesList();
        else navigate('/properties');
        break;
      case 'view_contacts':
        if (typeof showContactsView === 'function') showContactsView();
        else navigate('/contacts');
        break;
      case 'generate_pitch':
        showToast('🚧 Génération de pitch IA — bientôt disponible');
        break;
      default:
        showToast('Action non reconnue');
    }
  };

  // Récupérer les stats agrégées
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/api/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStats(prev => ({ ...(prev || {}), ...r.data,
          // lot CD — les deux réponses arrivent dans n'importe quel ordre : /dashboard/stats
          // ne doit jamais effacer des insights déjà reçus.
          insights_ia: prev?.insights_ia ?? r.data?.insights_ia }));
      } catch (e) {
        console.error('Dashboard stats KO', e);
      }
    };
    const fetchActionable = async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/api/dashboard/actionable`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setActionable(r.data);
      } catch (e) {
        console.error('Dashboard actionable KO', e);
      }
    };
    fetchStats();
    fetchActionable();
  }, []);

  // Les insights IA passent par gpt-4.1 : cache froid, c'est une quinzaine de secondes. Ils
  // ne doivent donc pas retarder les chiffres. Second appel, lancé après le premier rendu,
  // avec son propre état de chargement dans l'encart.
  const [insightsChargement, setInsightsChargement] = useState(true);
  useEffect(() => {
    let annule = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/api/dashboard/insights-ia`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!annule) setStats(prev => ({ ...(prev || {}), insights_ia: r.data.insights || [] }));
      } catch (e) {
        console.error('Dashboard insights KO', e);
      } finally {
        if (!annule) setInsightsChargement(false);
      }
    })();
    return () => { annule = true; };
  }, []);

  const regenerateInsights = async () => {
    if (regeneratingInsights) return;
    setRegeneratingInsights(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API}/api/dashboard/insights-ia/regenerate`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(prev => ({ ...(prev || {}), insights_ia: r.data.insights || [] }));
    } catch (e) {
      console.error('Regenerate insights KO', e);
    } finally {
      setRegeneratingInsights(false);
    }
  };

  const userName = getCurrentUserName();
  // Rentabilité calculée
  const rentabilite = useMemo(() => {
    const p = parseFloat(calcPrix) || 0;
    const l = parseFloat(calcLoyer) || 0;
    return p > 0 ? formatPercentFR((l / p) * 100, { decimals: 2 }) : formatPercentFR(0, { decimals: 2 });
  }, [calcLoyer, calcPrix]);

  // Répartition selon la clé sélectionnée
  const repartitionData = useMemo(() => {
    if (!stats) return [];
    const key = `repartition_${repartitionKey}`;
    return stats[key] || [];
  }, [stats, repartitionKey]);

  const repartitionTotal = repartitionData.reduce((acc, x) => acc + (x.count || 0), 0);

  // Derniers biens (5 premiers)
  const recentProperties = (properties || []).slice(0, 5);
  const recentContacts = (contacts || []).slice(0, 5);

  // Construire la liste des priorités (demandes + alertes)
  const alerts = [];
  // Demande entrantes = lead chaud
  (demandesContact || []).slice(0, 3).forEach(d => {
    alerts.push({
      kind: 'lead',
      title: d.nom || 'Nouveau contact',
      subtitle: d.message?.slice(0, 80) || 'Nouvelle demande',
      cta: 'Voir',
      onCta: showDemandesContactView,
    });
  });
  // Alertes sans photo — lot N4 : source UNIQUE = /dashboard/actionable (scan complet,
  // biens actifs sans `medias.photos`). Le calcul local ne portait que sur la liste déjà
  // chargée en mémoire et affichait donc un autre chiffre que le panneau IA, à 10 cm près.
  const biensSansPhoto = actionable?.biens_sans_photo ?? 0;
  if (biensSansPhoto > 0) {
    alerts.push({
      kind: 'alert',
      title: `${biensSansPhoto} bien${biensSansPhoto > 1 ? 's' : ''} sans photo`,
      subtitle: 'Leur visibilité est réduite sur les portails — ajoutez au moins 1 photo.',
      cta: 'Voir',
      onCta: () => goToPropertiesList?.('active'),
    });
  }

  const priorities = alerts.slice(0, 5);

  // Sous-titre « actions prioritaires » = somme réelle des chantiers du jour
  // (mandats à renouveler + leads chauds + RDV/tâches semaine + biens dormants).
  // lot N1 — tant que /dashboard/actionable n'a pas répondu, on n'affiche RIEN plutôt
  // qu'un total partiel qui change sous les yeux de l'utilisateur.
  const leadsCount = (demandesContact || []).length;
  const prioritairesCount = actionable
    ? (actionable.mandats_expirants_30j ?? 0)
      + leadsCount
      + (actionable.rdv_semaine ?? 0)
      + (actionable.biens_dormants_60j ?? 0)
    : null;

  return (
    <div className="p-6 md:p-8 max-w-[1600px] mx-auto space-y-8" style={BODY_FONT} data-testid="dashboard-root">
      {/* Toast pour actions non disponibles */}
      {toastMsg && (
        <div
          className="fixed top-6 right-6 z-[120] bg-slate-900 text-white px-4 py-3 rounded-lg shadow-2xl text-sm font-medium max-w-sm animate-in slide-in-from-top-4"
          data-testid="dashboard-toast"
        >
          {toastMsg}
        </div>
      )}
      {/* ═══ Section 1 : Greeting ═══ */}
      <header data-testid="dashboard-greeting">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={HEADING_FONT}>
          Bonjour {userName}, voici votre journée.
        </h1>
        <p className="text-slate-500 mt-1 text-sm" style={BODY_FONT}>
          {getTodayGreeting()}
          {prioritairesCount > 0 && ` · ${prioritairesCount} action${prioritairesCount > 1 ? 's' : ''} prioritaire${prioritairesCount > 1 ? 's' : ''}`}
        </p>
      </header>

      {/* ═══ Section 2 : Vue d'ensemble (KPIs) ═══ */}
      <section data-testid="dashboard-kpis">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider" style={HEADING_FONT}>
            Vue d'ensemble
          </h2>
          <span className="text-xs text-slate-400">Données en temps réel</span>
        </div>
        {/* lot CD — 2 colonnes dès 375 px */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          {(() => {
            const a = actionable || {};
            // lot N1 — un KPI, une source : /dashboard/actionable. Avant sa réponse on
            // affiche « — » plutôt qu'un chiffre issu d'un autre comptage (liste des biens),
            // qui donnait deux valeurs différentes pour le même KPI selon le moment du rendu.
            const chargé = !!actionable;
            const kpi = (v) => (chargé ? formatNumberFR(v ?? 0) : '—');
            const mand = a.mandats_expirants_30j ?? 0;
            const mand60 = a.mandats_expirants_60j ?? 0;
            const echus = a.mandats_echus ?? 0;
            const echusAnciens = a.mandats_echus_anciens ?? 0;
            const aff = a.affaires_en_cours ?? 0;
            const rdv = a.rdv_semaine ?? 0;
            const dorm = a.biens_dormants_60j ?? 0;
            const dormAnciens = a.biens_dormants_anciens ?? 0;
            // lot CD — lien vers les « anciens » (au-delà de 12 mois) : la carte garde son
            // action, le lien a la sienne (zone tactile ≥ 40 px).
            const lienAnciens = (n, texte, filtre) => (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); navigate(`/properties?filter=${filtre}`); }}
                className="mt-1 inline-flex items-center min-h-[40px] text-[11px] font-semibold text-blue-600 hover:underline"
                data-testid={`lien-${filtre}`}
              >
                + {formatNumberFR(n)} {texte}
              </button>
            );
            const diag = a.diagnostics_a_renouveler ?? 0;
            const diagThr = a.diagnostics_threshold_days ?? 90;
            return (
              <>
                {/* KPI 1 — Mandats à renouveler */}
                <KpiCard
                  testId="kpi-mandats-renouveler"
                  icon="bi-calendar-x"
                  iconBg={mand > 0 ? 'bg-amber-50' : 'bg-slate-100'}
                  iconColor={mand > 0 ? 'text-amber-600' : 'text-slate-500'}
                  label="Mandats à renouveler"
                  value={kpi(mand)}
                  sublabel={mand > 0
                    ? `dans les 30 prochains jours${mand60 > 0 ? ` · ${formatNumberFR(mand60)} de plus sous 60 j` : ''}`
                    : 'Aucun mandat à renouveler'}
                  onClick={mand > 0 ? () => navigate('/properties?filter=mandats_expirants_30j') : undefined}
                />
                {/* KPI 1 bis — Mandats échus (lot N3 : distinct de « à renouveler ») */}
                <KpiCard
                  testId="kpi-mandats-echus"
                  icon="bi-calendar-x-fill"
                  iconBg={echus > 0 ? 'bg-red-50' : 'bg-slate-100'}
                  iconColor={echus > 0 ? 'text-red-600' : 'text-slate-500'}
                  label="Mandats échus"
                  value={kpi(echus)}
                  sublabel={(
                    <>
                      {echus > 0
                        ? 'échéance dépassée, sur les 12 derniers mois'
                        : (echusAnciens === 0 ? 'Aucun mandat échu' : null)}
                      {echusAnciens > 0 && (
                        <div>{lienAnciens(echusAnciens, echusAnciens > 1 ? 'plus anciens' : 'plus ancien', 'mandats_echus_anciens')}</div>
                      )}
                    </>
                  )}
                  onClick={echus > 0 ? () => navigate('/properties?filter=mandats_echus') : undefined}
                />
                {/* KPI 2 — Biens actifs */}
                <KpiCard
                  testId="kpi-biens-actifs"
                  icon="bi-building" iconBg="bg-slate-100" iconColor="text-slate-500"
                  label="Biens actifs"
                  value={kpi(a.biens_actifs)}
                  sublabel="dans votre stock"
                  onClick={() => navigate('/properties?filter=actifs')}
                />
                {/* KPI 3 — Affaires en cours */}
                <KpiCard
                  testId="kpi-affaires-en-cours"
                  icon="bi-briefcase" iconBg="bg-slate-100" iconColor="text-slate-500"
                  label="Affaires en cours"
                  value={kpi(aff)}
                  sublabel={rdv > 0 ? `+ ${formatNumberFR(rdv)} RDV/tâches cette semaine` : 'Aucun RDV cette semaine'}
                  onClick={() => navigate('/properties?filter=affaires_en_cours')}
                />
                {/* KPI 4 — Biens dormants */}
                <KpiCard
                  testId="kpi-biens-dormants"
                  icon="bi-hourglass-split"
                  iconBg={dorm > 0 ? 'bg-amber-50' : 'bg-slate-100'}
                  iconColor={dorm > 0 ? 'text-amber-600' : 'text-slate-500'}
                  label="Biens dormants"
                  value={kpi(dorm)}
                  sublabel={(
                    <>
                      {dorm > 0
                        ? 'sans activité depuis 60 jours, sur 12 mois'
                        : (dormAnciens === 0 ? 'Stock parfaitement à jour' : null)}
                      {dormAnciens > 0 && (
                        <div>{lienAnciens(dormAnciens, dormAnciens > 1 ? 'inactifs depuis plus d\'un an' : 'inactif depuis plus d\'un an', 'dormants_anciens')}</div>
                      )}
                    </>
                  )}
                  onClick={dorm > 0 ? () => navigate('/properties?filter=dormants_60j') : undefined}
                />
                {/* KPI 5 — Diagnostics à renouveler (module socle Documents) */}
                <KpiCard
                  testId="kpi-diagnostics-renouveler"
                  icon="bi-clipboard2-pulse"
                  iconBg={diag > 0 ? 'bg-amber-50' : 'bg-slate-100'}
                  iconColor={diag > 0 ? 'text-amber-600' : 'text-slate-500'}
                  label="Diagnostics à renouveler"
                  value={kpi(diag)}
                  sublabel={diag > 0 ? `expirent sous ${diagThr} jours` : 'Aucun diagnostic à renouveler'}
                  onClick={diag > 0 ? () => navigate('/documents?expiring=1') : undefined}
                />
              </>
            );
          })()}
        </div>
      </section>

      {/* ═══ Section 3 : Priorités & Actions ═══ */}
      <section data-testid="dashboard-priorities">
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3" style={HEADING_FONT}>
          Priorités & Actions
        </h2>
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          {/* AJ5 — ce bloc compte les DEMANDES entrantes, pas des leads scorés : le titre le dit
              désormais. Un vrai compteur de leads chauds afficherait 0 (aucun contact n'est
              « chaud » en base) et un bloc vide serait pire que celui-ci. */}
          <div className="xl:col-span-8 bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center">
                  <i className="bi bi-fire text-orange-600"></i>
                </div>
                <h3 className="font-semibold text-slate-900" style={HEADING_FONT} data-testid="dashboard-demandes-titre">Demandes à traiter</h3>
                {priorities.length > 0 && (
                  <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">
                    {priorities.length} à traiter
                  </span>
                )}
              </div>
              {priorities.length > 0 && (
                <button onClick={showDemandesContactView} className="text-xs text-blue-600 font-medium hover:underline">
                  Voir tout →
                </button>
              )}
            </div>

            {priorities.length > 0 ? (
              <ul className="divide-y divide-slate-100">
                {priorities.map((p, i) => (
                  <li key={i} className="px-5 py-3.5 flex items-center gap-4 hover:bg-slate-50 transition-colors" data-testid={`priority-${i}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                      p.kind === 'lead' ? 'bg-gradient-to-br from-rose-400 to-orange-500' : 'bg-amber-100 text-amber-600'
                    }`}>
                      {p.kind === 'lead' ? getInitials(p.title) : <i className="bi bi-exclamation-triangle-fill"></i>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 text-sm truncate">{p.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5 truncate">{p.subtitle}</div>
                    </div>
                    <button onClick={p.onCta} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-medium rounded-lg transition-colors shrink-0">
                      {p.cta}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState
                icon="bi-check-circle"
                title="Tout est en ordre !"
                subtitle="Aucune demande en attente, aucune alerte. Profitez-en pour ajouter de nouveaux biens."
                cta="Ajouter un bien"
                onCta={startNewProperty}
              />
            )}
          </div>

          {/* Colonne IA + Matching */}
          <div className="xl:col-span-4 space-y-6">
            {/* Intelligence IA — sans insight : une seule ligne, carte blanche (lot CD) */}
            {!insightsChargement && (stats?.insights_ia || []).length === 0 ? (
              <div
                className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center gap-3"
                data-testid="dashboard-ai-insight"
              >
                <i className="bi bi-stars text-violet-600 text-lg"></i>
                <span className="text-sm text-slate-700 flex-1" style={BODY_FONT}>
                  Intelligence IA — rien à signaler pour l'instant
                </span>
                <button
                  onClick={regenerateInsights}
                  disabled={regeneratingInsights}
                  className="text-[11px] font-semibold text-violet-700 bg-violet-50 hover:bg-violet-100 disabled:opacity-50 px-2.5 py-1.5 rounded-lg transition flex items-center gap-1 shrink-0"
                  title="Régénérer les insights (cache 1h)"
                  data-testid="regenerate-insights-btn"
                >
                  <i className={`bi bi-arrow-clockwise ${regeneratingInsights ? 'animate-spin' : ''}`}></i>
                  {regeneratingInsights ? 'Analyse…' : 'Régénérer'}
                </button>
              </div>
            ) : (
            <div className="bg-gradient-to-br from-violet-600 to-indigo-700 text-white rounded-xl p-5 relative overflow-hidden" data-testid="dashboard-ai-insight">
              <div className="absolute -right-8 -top-8 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
              <div className="relative">
                <div className="flex items-center gap-2 mb-3">
                  <i className="bi bi-stars text-xl"></i>
                  <h3 className="font-semibold" style={HEADING_FONT}>Intelligence IA</h3>
                  <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded font-medium ml-2">Komerces IA</span>
                  <button
                    onClick={regenerateInsights}
                    disabled={regeneratingInsights}
                    className="ml-auto text-[11px] bg-white/15 hover:bg-white/25 disabled:opacity-50 disabled:cursor-not-allowed px-2 py-1 rounded font-medium transition flex items-center gap-1"
                    title="Régénérer les insights (cache 1h)"
                    data-testid="regenerate-insights-btn"
                  >
                    <i className={`bi bi-arrow-clockwise ${regeneratingInsights ? 'animate-spin' : ''}`}></i>
                    {regeneratingInsights ? 'Analyse…' : 'Régénérer'}
                  </button>
                </div>

                {insightsChargement ? (
                  <p className="text-sm opacity-75" data-testid="insights-chargement">
                    <i className="bi bi-stars animate-pulse mr-1"></i>
                    L&apos;IA analyse votre portefeuille…
                  </p>
                ) : (
                  <div className="space-y-2.5 max-h-[380px] overflow-y-auto -mx-1 px-1" data-testid="insights-list">
                    {(stats?.insights_ia || []).map((insight, idx) => {
                      const clickable = Boolean(insight.action_code);
                      return (
                        <div
                          key={idx}
                          role={clickable ? 'button' : undefined}
                          tabIndex={clickable ? 0 : undefined}
                          onClick={clickable ? () => handleInsightAction(insight) : undefined}
                          onKeyDown={clickable ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleInsightAction(insight);
                            }
                          } : undefined}
                          className={`bg-white/10 rounded-lg p-3 backdrop-blur-sm transition ${clickable ? 'hover:bg-white/20 cursor-pointer active:scale-[0.99]' : 'hover:bg-white/15'}`}
                          data-testid={`insight-${idx}`}
                          data-action-code={insight.action_code || 'none'}
                        >
                          <div className="flex items-start gap-2.5">
                            <div className="w-7 h-7 bg-white/20 rounded-md flex items-center justify-center shrink-0 mt-0.5">
                              <i className={`bi ${insight.icon || 'bi-stars'} text-sm`}></i>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[13px] font-semibold leading-tight" style={HEADING_FONT}>
                                {insight.titre || insight.title}
                              </p>
                              <p className="text-[11.5px] opacity-85 leading-snug mt-0.5">
                                {insight.detail}
                              </p>
                              {insight.action && (
                                <span className={`inline-block mt-1.5 text-[11px] font-medium px-2 py-0.5 rounded ${clickable ? 'bg-white/25 group-hover:bg-white/35' : 'bg-white/15 opacity-70'}`}>
                                  {insight.action} →
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Matching */}
            <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="dashboard-matching">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center">
                  <i className="bi bi-puzzle text-teal-600"></i>
                </div>
                <h3 className="font-semibold text-slate-900" style={HEADING_FONT}>Rapprochement</h3>
              </div>
              <div className="py-4 text-center">
                <i className="bi bi-puzzle text-3xl text-slate-300"></i>
                <p className="text-sm font-medium text-slate-700 mt-2" style={HEADING_FONT}>Pas de nouvelle correspondance</p>
                <p className="text-xs text-slate-500 mt-1">Créez de nouveaux biens pour générer des matchs.</p>
                <button onClick={startNewProperty} className="mt-3 text-xs font-medium text-blue-600 hover:underline">
                  Créer un bien →
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ Section 4 : Qualité & répartition ═══ */}
      <section data-testid="dashboard-pipeline">
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3" style={HEADING_FONT}>
          Qualité &amp; répartition
        </h2>
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          {/* Qualité des biens (lot CD — remplace « Pipeline commercial ») */}
          <div className="xl:col-span-8 bg-white border border-slate-200 rounded-xl p-5" data-testid="dashboard-qualite">
            {(() => {
              const a = actionable || {};
              const total = a.biens_a_completer ?? 0;
              const lignes = [
                { code: 'sans_photo', label: 'Sans photo', n: a.biens_sans_photo ?? 0, icon: 'bi-image' },
                { code: 'sans_description', label: 'Sans description', n: a.biens_sans_description ?? 0, icon: 'bi-card-text' },
                { code: 'sans_surface', label: 'Sans surface', n: a.biens_sans_surface ?? 0, icon: 'bi-rulers' },
              ];
              return (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${total > 0 ? 'bg-amber-50' : 'bg-emerald-50'}`}>
                        <i className={`bi ${total > 0 ? 'bi-exclamation-triangle text-amber-600' : 'bi-check-circle-fill text-emerald-600'}`}></i>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900" style={HEADING_FONT}>Qualité des biens</h3>
                        <p className="text-xs text-slate-500" style={BODY_FONT}>
                          Une annonce incomplète est moins visible sur les portails
                        </p>
                      </div>
                    </div>
                    {total > 0 && (
                      <span className="bg-amber-50 text-amber-700 px-2.5 py-1 rounded-md text-xs font-bold" data-testid="qualite-total">
                        {formatNumberFR(total)} bien{total > 1 ? 's' : ''} à compléter
                      </span>
                    )}
                  </div>

                  {total === 0 ? (
                    <div className="flex items-center gap-2 text-sm text-emerald-600 py-2" data-testid="qualite-vide" style={BODY_FONT}>
                      <i className="bi bi-check-circle-fill"></i>
                      Tous vos biens actifs sont complets
                    </div>
                  ) : (
                    <ul className="divide-y divide-slate-100">
                      {lignes.map((l) => (
                        <li key={l.code}>
                          <button
                            type="button"
                            onClick={() => navigate(`/properties?filter=${l.code}`)}
                            disabled={l.n === 0}
                            className="w-full flex items-center gap-3 py-3 text-left hover:bg-slate-50 disabled:hover:bg-transparent disabled:cursor-default rounded-lg px-2 -mx-2 transition-colors min-h-[44px]"
                            data-testid={`qualite-${l.code}`}
                          >
                            <i className={`bi ${l.icon} ${l.n > 0 ? 'text-amber-600' : 'text-slate-300'}`}></i>
                            <span className="flex-1 text-sm font-medium text-slate-700">{l.label}</span>
                            <span className={`text-sm font-semibold ${l.n > 0 ? 'text-slate-900' : 'text-slate-400'}`}>
                              {formatNumberFR(l.n)}
                            </span>
                            {l.n > 0 && <i className="bi bi-arrow-right text-slate-400 text-sm"></i>}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              );
            })()}
          </div>

          {/* Répartition (dropdown + donut) */}
          <div className="xl:col-span-4 bg-white border border-slate-200 rounded-xl p-5" data-testid="dashboard-repartition">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900" style={HEADING_FONT}>Répartition</h3>
              <select
                data-testid="repartition-selector"
                value={repartitionKey}
                onChange={(e) => setRepartitionKey(e.target.value)}
                className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white hover:bg-slate-50 font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="operation">Par transaction</option>
                <option value="objet">Par type de bien</option>
                <option value="profil">Par catégorie</option>
                <option value="activite">Par activité</option>
              </select>
            </div>

            {repartitionTotal > 0 ? (
              <>
                <DonutChart entries={repartitionData} total={repartitionTotal} />
                <ul className="space-y-1.5 text-sm">
                  {repartitionData.map((r, i) => {
                    const palette = ['#007BFF', '#10B981', '#F59E0B', '#0D9488', '#EC4899', '#06B6D4', '#F97316'];
                    return (
                      <li key={r.label} className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: palette[i % palette.length] }}></span>
                        <span className="flex-1 text-slate-700 truncate" title={r.label}>{r.label}</span>
                        <span className="font-medium text-slate-900">{r.count}</span>
                      </li>
                    );
                  })}
                </ul>
              </>
            ) : (
              <div className="py-8 text-center">
                <i className="bi bi-pie-chart text-3xl text-slate-300"></i>
                <p className="text-sm text-slate-500 mt-2">Aucune donnée à afficher</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ═══ Section 5 : Outils & Historique ═══ */}
      <section data-testid="dashboard-history">
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3" style={HEADING_FONT}>
          Outils & Historique
        </h2>
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          {/* Derniers ajouts */}
          <div className="xl:col-span-8 bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100">
              <h3 className="font-semibold text-slate-900" style={HEADING_FONT}>Derniers ajouts</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 md:divide-x divide-slate-100">
              {/* Biens */}
              <div className="p-5">
                <div className="text-xs font-medium text-slate-500 mb-3 flex items-center gap-1">
                  <i className="bi bi-building"></i>BIENS ({recentProperties.length})
                </div>
                {recentProperties.length > 0 ? (
                  <ul className="space-y-2.5">
                    {recentProperties.map((p, i) => (
                      <li key={p.id || i} className="flex items-center gap-3 text-sm cursor-pointer hover:bg-slate-50 -mx-2 px-2 py-1 rounded" onClick={() => goToPropertiesList?.('active')}>
                        <div className="w-8 h-8 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                          <i className="bi bi-shop text-slate-600 text-sm"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-900 truncate" title={p.bien_general?.titre_annonce || p.enseigne?.enseigne || p.reference || 'Bien sans titre'}>
                            {p.bien_general?.titre_annonce || p.enseigne?.enseigne || p.reference || 'Bien sans titre'}
                          </div>
                          <div className="text-xs text-slate-500 truncate">
                            {p.reference || ''}{p.bien_general?.ville_generale ? ` · ${p.bien_general.ville_generale}` : ''}
                          </div>
                        </div>
                        <i className="bi bi-arrow-right text-slate-400 text-sm"></i>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-slate-400 py-3">Aucun bien récent.</p>
                )}
                <button onClick={() => goToPropertiesList?.('active')} className="text-xs text-blue-600 hover:underline mt-3">Voir tous les biens →</button>
              </div>
              {/* Contacts */}
              <div className="p-5">
                <div className="text-xs font-medium text-slate-500 mb-3 flex items-center gap-1">
                  <i className="bi bi-people"></i>CONTACTS ({recentContacts.length})
                </div>
                {recentContacts.length > 0 ? (
                  <ul className="space-y-2.5">
                    {recentContacts.map((c, i) => {
                      const name = `${c.prenom || ''} ${c.nom || ''}`.trim() || c.email || 'Contact';
                      return (
                        <li key={c.id || i} className="flex items-center gap-3 text-sm cursor-pointer hover:bg-slate-50 -mx-2 px-2 py-1 rounded" onClick={showContactsView}>
                          <div className={`w-8 h-8 rounded-full ${degradeContact(name)} text-white flex items-center justify-center font-bold text-xs shrink-0`}>
                            {getInitials(name)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-slate-900 truncate">{name}</div>
                            <div className="text-xs text-slate-500 truncate">{c.type || c.groupes?.[0] || 'Contact'}</div>
                          </div>
                          <i className="bi bi-arrow-right text-slate-400 text-sm"></i>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="text-sm text-slate-400 py-3">Aucun contact récent.</p>
                )}
                <button onClick={showContactsView} className="text-xs text-blue-600 hover:underline mt-3">Voir tous les contacts →</button>
              </div>
            </div>
          </div>

          {/* Calculette */}
          <div className="xl:col-span-4 bg-white border border-slate-200 rounded-xl p-5" data-testid="dashboard-calculette">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                <i className="bi bi-calculator text-blue-600"></i>
              </div>
              <h3 className="font-semibold text-slate-900" style={HEADING_FONT}>Calculette Rentabilité</h3>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-500">Loyer annuel (€)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  data-testid="calc-loyer"
                  value={loyerFocused ? calcLoyer : (calcLoyer ? formatPriceFR(parseFloat(calcLoyer) || 0) : '')}
                  onFocus={() => setLoyerFocused(true)}
                  onBlur={() => setLoyerFocused(false)}
                  onChange={(e) => setCalcLoyer(e.target.value.replace(/[^\d.,]/g, '').replace(',', '.'))}
                  className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Prix FAI (€)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  data-testid="calc-prix"
                  value={prixFocused ? calcPrix : (calcPrix ? formatPriceFR(parseFloat(calcPrix) || 0) : '')}
                  onFocus={() => setPrixFocused(true)}
                  onBlur={() => setPrixFocused(false)}
                  onChange={(e) => setCalcPrix(e.target.value.replace(/[^\d.,]/g, '').replace(',', '.'))}
                  className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-4 text-center">
                <div className="text-xs font-medium text-emerald-700 uppercase tracking-wider">Rendement brut</div>
                <div className="text-3xl font-bold text-emerald-700 mt-1 num" style={HEADING_FONT}>{rentabilite}</div>
              </div>
              <button
                onClick={startNewProperty}
                data-testid="calc-cta"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#0062CC] hover:bg-[#0056b3] text-white px-4 py-2.5 rounded-lg font-medium text-sm transition-colors"
              >
                <i className="bi bi-house-add"></i>Créer ce bien
              </button>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};
export default Dashboard;
